import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import portalApi from '../../../../lib/portalApi';
import KraKpiChart from '../components/KraKpiChart';
import {
  Building2,
  CheckCircle2,
  Clock,
  Calendar as CalendarIcon,
  ArrowRight,
  TrendingUp,
} from 'lucide-react';

export default function MentorOverviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('this_month');
  const [type, setType] = useState('all');

  const loadData = async (selectedPeriod = period, selectedType = type) => {
    try {
      setLoading(true);
      setError('');
      const res = await portalApi.getMentorOverview({ period: selectedPeriod, type: selectedType });
      setData(res);
    } catch (err) {
      console.error('Failed to load mentor overview:', err);
      setError(err.message || 'Failed to load mentor dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(period, type);
  }, [period, type]);

  const cards = data?.summaryCards || {
    assignedStartups: 0,
    kraKpiDoneThisMonth: 0,
    pendingTasks: 0,
    meetingsThisWeek: 0,
  };

  const chart = data?.kraKpiChart || {
    headlinePercentage: null,
    numerator: 0,
    denominator: 0,
    buckets: [],
  };

  const startups = data?.myStartups || [];
  const tasks = data?.myTasks || [];

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Top Greeting */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#F2F2F4] tracking-tight">
            Good morning, {user?.fullName || user?.name || 'Mentor'}
          </h1>
          <p className="text-sm text-[#8E8E99] mt-1">
            Here's what needs your attention today in your mentor workspace.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/mentor-dashboard/calendar"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#321129] hover:bg-[#321129]/80 border border-[#D42B92]/30 text-[#D42B92] text-xs font-semibold transition-colors"
          >
            <CalendarIcon className="w-3.5 h-3.5" />
            <span>Open Calendar</span>
          </Link>
          <Link
            to="/mentor-dashboard/tasks"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white text-xs font-semibold transition-colors shadow-lg shadow-[#D42B92]/20"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>My Tasks Board</span>
          </Link>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* 1. Live Summary Cards matching PDF Page 3 Box 1 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Assigned Startups */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 hover:border-[#D42B92]/40 transition-colors group">
          <div className="flex items-center justify-between text-[#8E8E99] mb-3">
            <span className="text-xs font-medium uppercase tracking-wider">Assigned Startups</span>
            <div className="p-2 rounded-xl bg-white/[0.03] group-hover:bg-[#D42B92]/10 group-hover:text-[#D42B92] transition-colors">
              <Building2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-[#F2F2F4]">
            {loading ? '…' : cards.assignedStartups}
          </div>
          <div className="text-xs text-[#8E8E99] mt-1 flex items-center gap-1">
            <span className="text-emerald-400">●</span> Your active portfolio
          </div>
        </div>

        {/* Card 2: KRA/KPI tasks done this month */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 hover:border-[#D42B92]/40 transition-colors group">
          <div className="flex items-center justify-between text-[#8E8E99] mb-3">
            <span className="text-xs font-medium uppercase tracking-wider">Done This Month</span>
            <div className="p-2 rounded-xl bg-white/[0.03] group-hover:bg-[#D42B92]/10 group-hover:text-[#D42B92] transition-colors">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-[#F2F2F4]">
            {loading ? '…' : cards.kraKpiDoneThisMonth}
          </div>
          <div className="text-xs text-[#8E8E99] mt-1 flex items-center gap-1">
            <span className="text-emerald-400">●</span> Completed KRA / KPI work
          </div>
        </div>

        {/* Card 3: Pending tasks */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 hover:border-[#D42B92]/40 transition-colors group">
          <div className="flex items-center justify-between text-[#8E8E99] mb-3">
            <span className="text-xs font-medium uppercase tracking-wider">Pending Tasks</span>
            <div className="p-2 rounded-xl bg-white/[0.03] group-hover:bg-[#D42B92]/10 group-hover:text-[#D42B92] transition-colors">
              <Clock className="w-4 h-4 text-amber-400" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-[#F2F2F4]">
            {loading ? '…' : cards.pendingTasks}
          </div>
          <div className="text-xs text-[#8E8E99] mt-1 flex items-center gap-1">
            <span className="text-amber-400">●</span> In progress or to do
          </div>
        </div>

        {/* Card 4: Meetings this week */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 hover:border-[#D42B92]/40 transition-colors group">
          <div className="flex items-center justify-between text-[#8E8E99] mb-3">
            <span className="text-xs font-medium uppercase tracking-wider">Meetings This Week</span>
            <div className="p-2 rounded-xl bg-white/[0.03] group-hover:bg-[#D42B92]/10 group-hover:text-[#D42B92] transition-colors">
              <CalendarIcon className="w-4 h-4 text-sky-400" />
            </div>
          </div>
          <div className="text-3xl font-extrabold text-[#F2F2F4]">
            {loading ? '…' : cards.meetingsThisWeek}
          </div>
          <div className="text-xs text-[#8E8E99] mt-1 flex items-center gap-1">
            <span className="text-sky-400">●</span> Scheduled business week
          </div>
        </div>
      </div>

      {/* 2. KRA/KPI Completion Graph matching PDF Page 3 Box 2 */}
      <KraKpiChart
        buckets={chart.buckets}
        headlinePercentage={chart.headlinePercentage}
        numerator={chart.numerator}
        denominator={chart.denominator}
        period={period}
        type={type}
        onPeriodChange={(newPeriod) => setPeriod(newPeriod)}
        onTypeChange={(newType) => setType(newType)}
      />

      {/* 3 & 4. Bottom Grid: My Startups & My Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Box 3: My Startups Table */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#D42B92]" />
              <h2 className="text-base font-bold text-[#F2F2F4]">My Startups</h2>
            </div>
            <Link
              to="/mentor-dashboard/startups"
              className="text-xs font-medium text-[#D42B92] hover:text-[#ff8ec5] flex items-center gap-1 transition-colors"
            >
              <span>View All Startups</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {startups.length === 0 ? (
            <div className="py-12 text-center text-xs text-[#8E8E99]">
              No startups assigned yet. Please contact the admin.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-[#8E8E99] border-b border-[#2C2C34]/60">
                    <th className="pb-3 font-semibold uppercase tracking-wider">Startup</th>
                    <th className="pb-3 font-semibold uppercase tracking-wider">Founder</th>
                    <th className="pb-3 font-semibold uppercase tracking-wider">Stage</th>
                    <th className="pb-3 font-semibold uppercase tracking-wider text-right">Progress</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2C2C34]/30">
                  {startups.map((s) => (
                    <tr
                      key={s.id}
                      onClick={() => navigate(`/mentor-dashboard/startup/${s.id}`)}
                      className="group cursor-pointer hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="py-3 font-semibold text-[#F2F2F4] group-hover:text-[#D42B92] transition-colors">
                        {s.name}
                      </td>
                      <td className="py-3 text-[#8E8E99]">{s.founderName}</td>
                      <td className="py-3">
                        <span className="px-2 py-0.5 rounded-md bg-white/[0.04] text-[#8E8E99] border border-[#2C2C34]">
                          {s.stage}
                        </span>
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 bg-[#2C2C34] h-1.5 rounded-full overflow-hidden">
                            <div
                              className="bg-[#D42B92] h-full rounded-full"
                              style={{ width: `${s.progress}%` }}
                            />
                          </div>
                          <span className="text-[#F2F2F4] font-medium">{s.progress}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Box 4: My Tasks Preview */}
        <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl p-5 sm:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#D42B92]" />
              <h2 className="text-base font-bold text-[#F2F2F4]">My Tasks</h2>
            </div>
            <Link
              to="/mentor-dashboard/tasks"
              className="text-xs font-medium text-[#D42B92] hover:text-[#ff8ec5] flex items-center gap-1 transition-colors"
            >
              <span>View All Tasks</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {tasks.length === 0 ? (
            <div className="py-12 text-center text-xs text-[#8E8E99]">
              No active tasks or performance goals assigned.
            </div>
          ) : (
            <div className="space-y-2">
              {tasks.slice(0, 5).map((t) => (
                <div
                  key={t._id}
                  onClick={() => navigate('/mentor-dashboard/tasks')}
                  className="p-3 rounded-xl bg-[#08080A] border border-[#2C2C34] hover:border-[#D42B92]/40 cursor-pointer flex items-center justify-between gap-3 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-[#F2F2F4] truncate">{t.title}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          t.kind === 'KRA'
                            ? 'bg-purple-500/20 text-purple-300'
                            : t.kind === 'KPI'
                            ? 'bg-sky-500/20 text-sky-300'
                            : 'bg-emerald-500/20 text-emerald-300'
                        }`}
                      >
                        {t.kind}
                      </span>
                      <span className="text-[11px] text-[#8E8E99]">
                        {t.dueDate ? `Due ${new Date(t.dueDate).toLocaleDateString()}` : 'No deadline'}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      t.status === 'Done'
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : t.status === 'In Progress'
                        ? 'bg-sky-500/10 text-sky-400'
                        : t.status === 'On Hold'
                        ? 'bg-amber-500/10 text-amber-400'
                        : 'bg-white/5 text-[#8E8E99]'
                    }`}
                  >
                    {t.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
